const DB_NAME = "agility";
const DB_VERSION = 4;
let dbPromise = null;
function open() {
    if (dbPromise)
        return dbPromise;
    dbPromise = openFresh();
    return dbPromise;
}
function openFresh() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            console.info(`[db] upgrade needed for ${DB_NAME} v${DB_VERSION}`);
            const db = req.result;
            if (!db.objectStoreNames.contains("baseline")) {
                db.createObjectStore("baseline");
            }
            if (!db.objectStoreNames.contains("prefs")) {
                db.createObjectStore("prefs");
            }
            if (!db.objectStoreNames.contains("progress")) {
                db.createObjectStore("progress");
            }
            if (!db.objectStoreNames.contains("phoneme-libraries")) {
                db.createObjectStore("phoneme-libraries", { keyPath: "id" });
            }
            if (!db.objectStoreNames.contains("active-library")) {
                db.createObjectStore("active-library");
            }
            // v3: introduce the consumer (app) namespace dimension. Existing
            // un-namespaced records logically belong to the default app
            // (Beatbox Hero); because the default app id maps to the bare key
            // (see storage/namespace.ts appScopedKey), no key rewrite is needed
            // and current users lose nothing. We only record that the migration
            // ran so future app ids get their own namespaced keys.
            if (req.transaction) {
                try {
                    const prefs = req.transaction.objectStore("prefs");
                    prefs.put({ migratedAt: Date.now(), toVersion: DB_VERSION }, "__app-namespace-migration");
                    console.info("[db] v3 migration: existing records mapped into default " +
                        "app namespace (no key rewrite required)");
                }
                catch (err) {
                    console.warn("[db] v3 migration bookkeeping failed", err);
                }
            }
            // v4: introduce recording sessions/chunks/envelopes stores to support
            // durable, resumable audio recording pipelines.
            if (!db.objectStoreNames.contains("recording-sessions")) {
                db.createObjectStore("recording-sessions", { keyPath: "id" });
            }
            if (!db.objectStoreNames.contains("recording-chunks")) {
                const chunkStore = db.createObjectStore("recording-chunks", {
                    keyPath: "id",
                });
                chunkStore.createIndex("sessionId-chunkIndex", ["sessionId", "chunkIndex"], { unique: false });
                chunkStore.createIndex("sessionId", "sessionId", { unique: false });
            }
            else {
                console.info("[db] recording-chunks store already exists");
            }
            if (!db.objectStoreNames.contains("recording-envelopes")) {
                db.createObjectStore("recording-envelopes", { keyPath: "sessionId" });
            }
        };
        req.onsuccess = () => {
            const db = req.result;
            db.onclose = () => {
                dbPromise = null;
            };
            resolve(db);
        };
        req.onerror = () => {
            console.error("[db] open failed", req.error);
            dbPromise = null;
            reject(req.error);
        };
    });
}
/**
 * Get all records from `storeName` whose value at `indexName` equals
 * `value`. Requires the store to have an index named `indexName`.
 */
export async function getAllByIndex(storeName, indexName, value) {
    const db = await open();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const req = tx.objectStore(storeName).index(indexName).getAll(value);
        req.onsuccess = () => resolve(req.result ?? []);
        req.onerror = () => reject(req.error);
    });
}
/**
 * Get the first record from `storeName` whose value at `indexName` equals
 * `value`, or `undefined` if none is found.
 */
export async function getByIndex(storeName, indexName, value) {
    const db = await open();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const req = tx.objectStore(storeName).index(indexName).get(value);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}
/**
 * Delete all records from `storeName` whose value at `indexName` equals
 * `value`.
 */
export async function delByIndex(storeName, indexName, value) {
    const db = await open();
    await new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        const index = store.index(indexName);
        const cursorReq = index.openKeyCursor(value);
        cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (cursor) {
                store.delete(cursor.primaryKey);
                cursor.continue();
            }
        };
        cursorReq.onerror = () => reject(cursorReq.error);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
/**
 * Persist multiple records to `storeName` in a single transaction. Each
 * value must already carry its keyPath property (no explicit key arg is
 * used), matching stores created with a `keyPath` option.
 */
export async function putBulk(storeName, values) {
    if (values.length === 0)
        return;
    const db = await open();
    await new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        for (const value of values) {
            store.put(value);
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
export async function put(storeName, key, value) {
    const db = await open();
    await new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        tx.objectStore(storeName).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
export async function get(storeName, key) {
    const db = await open();
    const value = await new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const req = tx.objectStore(storeName).get(key);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return value;
}
export async function putRecord(storeName, value) {
    const db = await open();
    await new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        tx.objectStore(storeName).put(value);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
export async function getAll(storeName) {
    const db = await open();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const req = tx.objectStore(storeName).getAll();
        req.onsuccess = () => resolve(req.result ?? []);
        req.onerror = () => reject(req.error);
    });
}
export async function del(storeName, key) {
    const db = await open();
    await new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        tx.objectStore(storeName).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
